import React from "react";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

/**
 * Shown for as long as a spec sheet read is in flight.
 *
 * The read runs server-side and takes 15-50s. A spinner on a toolbar icon was
 * far too quiet for that: it is easy to miss and easy to wander off from, and
 * an operator who navigates away loses sight of a result that has already been
 * paid for. This takes the screen so the wait is unmistakable.
 *
 * It is driven by savingPhotoAI, which the resume epic sets again when it finds
 * a job still running, so the overlay comes back by itself after a reload.
 */

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${(p) => p.theme.spacing.md};
  padding: ${(p) => p.theme.spacing.lg} ${(p) => p.theme.spacing.md};
  font-family: ${(p) => p.theme.font.family};
`;

const Title = styled.div`
  font-size: ${(p) => p.theme.font.sizeLg};
  font-weight: ${(p) => p.theme.font.weightBold};
  color: ${(p) => p.theme.color.primary};
`;

const Detail = styled.div`
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.textMuted};
  line-height: 1.5;
  max-width: 30em;
`;

const Wait = styled.div`
  background: ${(p) => p.theme.color.surfaceAlt};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.text};
`;

const ScanProgressOverlay = ({ show }) => {
  const { t } = useTranslation();

  return (
    <Modal
      show={!!show}
      centered
      // No close button and a static backdrop: the point is that the operator
      // does not wander off mid-read.
      backdrop="static"
      keyboard={false}
    >
      <Modal.Body>
        <Body>
          <Spinner animation="border" variant="primary" />
          <Title>{t("specsheetScan.readingTitle")}</Title>
          <Detail>{t("specsheetScan.readingDetail")}</Detail>
          <Wait>{t("specsheetScan.readingStay")}</Wait>
        </Body>
      </Modal.Body>
    </Modal>
  );
};

export default ScanProgressOverlay;
